import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  dateFilters: {},
  propertyFilters: {},
};

/*
 * Will eventually look like
 * {
 *   callLogPage: {
 *     direction: [
 *       { name: 'incoming', value: true },
 *       { name: 'outgoing', value: false },
 *     ],
 *   },
 *
 *   leadsPage: {
 *     temperature: [
 *       { name: 'Hot', value: true },
 *       { name: 'Warm', value: true },
 *       { name: 'Cold', value: false },
 *       { name: 'Unknown', value: false },
 *       { name: 'Archived', value: false },
 *     ],
 *     referralType: [
 *       { name: 'Company / Organization', value: true },
 *       { name: 'Word of Mouth', value: true },
 *       { name: 'Internet Search', value: true },
 *       { name: 'Social Media', value: true },
 *       { name: 'Media', value: false },
 *       { name: 'Marketing Event/Campaign', value: true },
 *       { name: 'Unknown', value: false },
 *     ],
 *   },
 *
 *   prospectsPage: {
 *     temperature: [
 *       { name: 'Hot', value: true },
 *       { name: 'Warm', value: true },
 *       { name: 'Cold', value: false },
 *       { name: 'Unknown', value: false },
 *       { name: 'Archived', value: false },
 *     ],
 *     salesStage: [
 *       { name: 'Initial Contact', value: true },
 *       { name: 'Admitted Client', value: false },
 *     ],
 *     owner: [
 *       { id: 0, name: 'Unassigned', value: false },
 *       { id: 123, name: 'Florence Nightingale', value: false },
 *       { id: 124, name: 'Gregory House', value: true },
 *       { id: 125, name: 'Ann Sullivan', value: false },
 *     ],
 *     facility: [
 *       { name: 'Unassigned', value: false },
 *       { name: 'Healing Touch SD Road', value: true },
 *       { name: 'JBL', value: true },
 *     ],
 *   },
 *
 *   clientsPage: {
 *     owner: [
 *       { id: 0, name: 'Unassigned', value: false },
 *       { id: 123, name: 'Florence Nightingale', value: false },
 *       { id: 124, name: 'Gregory House', value: true },
 *       { id: 125, name: 'Ann Sullivan', value: false },
 *     ],
 *     facility: [
 *       { name: 'Unassigned', value: false },
 *       { name: 'Healing Touch SD Road', value: true },
 *       { name: 'JBL', value: true },
 *     ],
 *   },
 *
 *   alumniPage: {
 *     owner: [
 *       { id: 0, name: 'Unassigned', value: false },
 *       { id: 123, name: 'Florence Nightingale', value: false },
 *       { id: 124, name: 'Gregory House', value: true },
 *       { id: 125, name: 'Ann Sullivan', value: false },
 *     ],
 *     facility: [
 *       { name: 'Unassigned', value: false },
 *       { name: 'Healing Touch SD Road', value: true },
 *       { name: 'JBL', value: true },
 *     ],
 *   },
 *
 *   referralCompaniesPage: {
 *     temperature: [
 *       { name: 'Hot', value: true },
 *       { name: 'Warm', value: true },
 *       { name: 'Cold', value: false },
 *       { name: 'Unknown', value: false },
 *       { name: 'Archived', value: false },
 *     ],
 *   },
 *
 *   referralContactsPage: {
 *     owner: [
 *       { id: 0, name: 'Unassigned', value: false },
 *       { id: 123, name: 'Florence Nightingale', value: false },
 *       { id: 124, name: 'Gregory House', value: true },
 *       { id: 125, name: 'Ann Sullivan', value: false },
 *     ],
 *     temperature: [
 *       { name: 'Hot', value: true },
 *       { name: 'Warm', value: true },
 *       { name: 'Cold', value: false },
 *       { name: 'Unknown', value: false },
 *       { name: 'Archived', value: false },
 *     ],
 *   },
 * }
 */

function setPropertyFilterStateInPage(pageState, filterName, menuItem, value) {
  const filterState = pageState[filterName] || [];
  const others = filterState.filter((item) => item.name !== menuItem);
  const all = [...others, { name: menuItem, value: value }];
  return { ...pageState, [filterName]: all };
}

function setPersonFilterStateInPage(pageState, filterName, personId, personName, value) {
  const filterState = pageState[filterName] || [];
  const others = filterState.filter((item) => item.id !== personId);
  const all = [...others, { id: personId, name: personName, value: value }];
  return { ...pageState, [filterName]: all };
}

export const filterStatesSlice = createSlice({
  name: 'filterStates',
  initialState: initialState,
  reducers: {
    clearFilterStates: () => {
      return initialState;
    },

    setPropertyFilterState: (state, action) => {
      const { pageName, filterName, propertyName, value } = action.payload;
      const pageState = state.propertyFilters[pageName] || {};
      const updatedPageState = setPropertyFilterStateInPage(pageState, filterName, propertyName, value);
      const propertyFilters = { ...state.propertyFilters, [pageName]: updatedPageState };
      return { ...state, propertyFilters };
    },

    setPersonFilterState: (state, action) => {
      const { pageName, filterName, personId, personName, value } = action.payload;
      const pageState = state.propertyFilters[pageName] || {};
      const updatedPageState = setPersonFilterStateInPage(pageState, filterName, personId, personName, value);
      const propertyFilters = { ...state.propertyFilters, [pageName]: updatedPageState };
      return { ...state, propertyFilters };
    },

    addMenuItem: (state, action) => {
      const { pageName, filterName, menuItem } = action.payload;
      const pageState = state[pageName] || {};
      const updatedPageState = setPropertyFilterStateInPage(pageState, filterName, menuItem, true);
      return { ...state, [pageName]: updatedPageState };
    },
  },
});

export const { clearFilterStates, setPropertyFilterState, setPersonFilterState, addMenuItem } =
  filterStatesSlice.actions;

export default filterStatesSlice.reducer;
